import { useContext, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, IconButton, Typography, Switch } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Cookie from '@mui/icons-material/Cookie';
import Edit from '@mui/icons-material/Edit';
import FingerprintOutlined from '@mui/icons-material/FingerprintOutlined';
import Logout from '@mui/icons-material/Logout';

import { Popup } from '@/components/common/Popup';
import { ctxUser } from '@/contexts/UserContext';
import type { BiometicLoginSupport } from '@/types';
import { checkCookiebot } from '@/utilities/checkCookiebot';
import { isNotTokenUser } from '@/utilities/preconditions';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import LanguageSelect from './LanguageSelect';
import {
  SettingsGroupTitle,
  settingsIconSx,
  SettingsPaper,
  SettingsPaperTitle,
  settingsStartIconSx,
} from './SettingsComponents.style';
import {
  CommonPageContainer,
  CommonPageHeader,
  CommonPageScrollableContent,
} from '../common/CommonPageComponents.style';

function openCookieManager() {
  Cookiebot.renew();
}

export default function ProfilePopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ctx = useContext(ctxUser);
  const [bio, setBio] = useState<BiometicLoginSupport>(() => {
    const biometric = localStorage.getItem('biometriclogin');
    if (biometric === null) return 'false';
    if (biometric === 'notsupported') return 'notsupported';
    if (biometric === 'off') return 'off';
    return biometric === 'true' ? 'true' : 'false';
  });

  function handleBioChange(_event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    setBio(checked ? 'true' : 'off');
    localStorage.setItem('biometriclogin', checked ? 'true' : 'off');
  }

  if (!ctx) return;

  const title = t('mainMenu.profile');
  const user = ctx.user;

  const body = (
    <CommonPageScrollableContent>
      {isNotTokenUser(ctx) && (
        <div>
          <SettingsGroupTitle data-qa-element-id="profile_title_label">{t('settings.profile')}</SettingsGroupTitle>
          <SettingsPaper data-qa-element-id="profile_paper" sx={{ display: 'block', overflow: 'hidden', p: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <AccountCircle sx={settingsStartIconSx} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography data-qa-element-id="username_label">{user.name}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }} data-qa-element-id="email_label">
                  {user.email}
                </Typography>
              </Box>
              <IconButton
                aria-label={t('mainMenu.logout')}
                onClick={async () => await navigate('/logout')}
                data-qa-element-id="profle_logout_button"
              >
                <Logout sx={settingsIconSx} />
              </IconButton>
            </Box>
            <ButtonBase
              onClick={() => void navigate('../password-change')}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                borderTop: 1,
                borderColor: 'divider',
                px: 2,
                py: 1.25,
                textAlign: 'left',
              }}
              data-qa-element-id="change_password_button"
            >
              <Typography component="span" sx={{ color: 'primary.main' }}>
                {t('settings.changePassword')}
              </Typography>
            </ButtonBase>
          </SettingsPaper>
        </div>
      )}
      <div>
        <SettingsGroupTitle data-qa-element-id="language_title_label">{t('settings.language')}</SettingsGroupTitle>
        <LanguageSelect />
      </div>
      {bio !== 'notsupported' && isNotTokenUser(ctx) && (
        <div>
          <SettingsGroupTitle data-qa-element-id="biometrics_title_label">
            {t('settings.biometrics')}
          </SettingsGroupTitle>
          <SettingsPaper data-qa-element-id="biometrics_paper">
            <FingerprintOutlined sx={settingsStartIconSx} />

            <SettingsPaperTitle data-qa-element-id="biometrics_label">
              {t('settings.biometricAuthentication')}
            </SettingsPaperTitle>
            <Switch
              color="secondary"
              checked={bio !== 'off'}
              onChange={handleBioChange}
              data-qa-element-id="biometrics_swich_button"
            />
          </SettingsPaper>
        </div>
      )}
      {checkCookiebot() && isNotTokenUser(ctx) && (
        <div>
          <SettingsGroupTitle data-qa-element-id="cookies_title_label">{t('settings.cookies')}</SettingsGroupTitle>
          <ButtonBase
            onClick={openCookieManager}
            data-qa-element-id="cookies_paper"
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              alignItems: 'center',
              p: 2,
              mb: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              boxShadow: '0px -1px 0px rgba(0, 0, 0, 0.12) inset',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              textAlign: 'left',
              transition: 'background-color .2s, box-shadow .2s',
              '&:hover': {
                backgroundColor: 'action.hover',
                boxShadow: 4,
              },
            }}
          >
            <Cookie sx={settingsStartIconSx} />

            <SettingsPaperTitle data-qa-element-id="cookies_label">{t('settings.cookieManagement')}</SettingsPaperTitle>

            <Edit sx={settingsIconSx} />
          </ButtonBase>
        </div>
      )}
    </CommonPageScrollableContent>
  );

  if (isPhoneOrHighScreen()) {
    return (
      <Popup
        popupTitle={title}
        header={undefined}
        body={body}
        footer={undefined}
        closePopup={closePopup}
        isFullScr={true}
        showCloseButton={true}
        leftText={true}
      />
    );
  }

  return (
    <CommonPageContainer data-qa-element-id="settings_view">
      <CommonPageHeader data-qa-element-id="settings_view_title">{title}</CommonPageHeader>
      {body}
    </CommonPageContainer>
  );
}
